import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminVerify } from '../../services/api';
import { listMembershipPlans } from '../../services/api';
import { Save, Trash2, Edit2, ArrowLeft, Plus, X, Check, Search, Filter } from 'lucide-react';
import { DEFAULT_BASE_URL } from '../../services/api';
import { getCurrentLanguage } from '../../services/dataSync';
const LANGUAGES = ['en', 'mr', 'hi'];
const LANGUAGE_LABELS = { en: 'English', mr: 'Marathi', hi: 'Hindi' };
const emptyLocalizedText = { en: '', mr: '', hi: '' };
const emptyTrainingProgramTranslations = {
  trainingAreas: { mr: [], hi: [] },
  outcomes: { mr: [], hi: [] }
};
const emptyForm = {
  name: { ...emptyLocalizedText },
  type: 'unified',
  alternativeName: { ...emptyLocalizedText },
  description: { ...emptyLocalizedText },
  pricingModel: 'worker-based',
  smallIndustryThreshold: 10,
  smallIndustryPrice: 5000,
  perWorkerPrice: 100,
  duration: 12,
  durationUnit: 'Months',
  features: { ...emptyLocalizedText },
  // Training program
  trainingProgram: {
    enabled: false,
    duration: '52-Weeks',
    modes: ['online', 'offline', 'hybrid'],
    trainingAreas: [],
    modules: [],
    skillMapping: [],
    outcomes: []
  },
  trainingProgramTranslations: { ...emptyTrainingProgramTranslations },
  isActive: true
};

const AdminMembershipPlansPage = () => {
  const [items, setItems] = useState([]);
  const [allItems, setAllItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [showFormModal, setShowFormModal] = useState(false);
  const [activeLang, setActiveLang] = useState('en');
  const [currentLang, setCurrentLang] = useState(getCurrentLanguage());
  const [filters, setFilters] = useState({
    name: '',
    isActive: ''
  });
  const navigate = useNavigate();

  useEffect(() => {
    const init = async () => {
      try {
        await adminVerify();
        await refresh();
      } catch (e) {
        navigate('/admin');
      }
    };
    init();
  }, [navigate]);

  useEffect(() => {
    const handleLanguageChange = () => setCurrentLang(getCurrentLanguage());
    window.addEventListener('languageChanged', handleLanguageChange);
    window.addEventListener('storage', handleLanguageChange);
    return () => {
      window.removeEventListener('languageChanged', handleLanguageChange);
      window.removeEventListener('storage', handleLanguageChange);
    };
  }, []);

  async function refresh() {
    setLoading(true);
    setError('');
    setSuccess('');
    try {
      const data = await listMembershipPlans();
      const plans = Array.isArray(data) ? data : [];
      setAllItems(plans);
      applyFilters(plans);
    } catch (e) {
      setError(e.message || 'Failed to load plans');
      setAllItems([]);
      setItems([]);
    } finally {
      setLoading(false);
    }
  }

  function applyFilters(plansToFilter = null) {
    const plans = plansToFilter || allItems;
    if (!plans || plans.length === 0) {
      setItems([]);
      return;
    }

    let filtered = [...plans];

    // Filter by name
    if (filters.name) {
      const nameLower = filters.name.toLowerCase();
      filtered = filtered.filter(plan => 
        [getLocalizedValue(plan, 'name', 'en'), getLocalizedValue(plan, 'name', 'mr'), getLocalizedValue(plan, 'name', 'hi'),
          getLocalizedValue(plan, 'alternativeName', 'en'), getLocalizedValue(plan, 'alternativeName', 'mr'), getLocalizedValue(plan, 'alternativeName', 'hi')]
          .some(v => String(v || '').toLowerCase().includes(nameLower))
      );
    }

    // Filter by active status
    if (filters.isActive !== '') {
      const isActive = filters.isActive === 'true';
      filtered = filtered.filter(plan => plan.isActive === isActive);
    }

    setItems(filtered);
  }

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const clearFilters = () => {
    setFilters({
      name: '',
      isActive: ''
    });
  };

  const activeFiltersCount = Object.values(filters).filter(v => v !== '').length;

  useEffect(() => {
    if (allItems.length > 0 || Object.values(filters).some(v => v !== '')) {
      applyFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, allItems]);

  function toFeatureArray(text) {
    if (!text) return [];
    return text.split('\n').map(s => s.trim()).filter(Boolean);
  }

  function normalizeLocalizedText(primaryValue, translationValue) {
    if (primaryValue && typeof primaryValue === 'object' && !Array.isArray(primaryValue)) {
      return {
        en: primaryValue.en || '',
        mr: primaryValue.mr || '',
        hi: primaryValue.hi || ''
      };
    }
    return {
      en: primaryValue || '',
      mr: translationValue?.mr || '',
      hi: translationValue?.hi || ''
    };
  }

  function normalizeLocalizedFeatures(primaryValue, translationValue) {
    const primaryText = Array.isArray(primaryValue) ? primaryValue.join('\n') : (primaryValue || '');
    return {
      en: primaryText,
      mr: Array.isArray(translationValue?.mr) ? translationValue.mr.join('\n') : '',
      hi: Array.isArray(translationValue?.hi) ? translationValue.hi.join('\n') : ''
    };
  }

  function normalizeTrainingProgramTranslations(trainingProgramTranslationValue) {
    return {
      trainingAreas: {
        mr: Array.isArray(trainingProgramTranslationValue?.trainingAreas?.mr) ? trainingProgramTranslationValue.trainingAreas.mr : [],
        hi: Array.isArray(trainingProgramTranslationValue?.trainingAreas?.hi) ? trainingProgramTranslationValue.trainingAreas.hi : []
      },
      outcomes: {
        mr: Array.isArray(trainingProgramTranslationValue?.outcomes?.mr) ? trainingProgramTranslationValue.outcomes.mr : [],
        hi: Array.isArray(trainingProgramTranslationValue?.outcomes?.hi) ? trainingProgramTranslationValue.outcomes.hi : []
      }
    };
  }

  function getLocalizedValue(item, field, lang = currentLang) {
    const primary = item?.[field];
    const translations = item?.translations?.[field];
    if (primary && typeof primary === 'object' && !Array.isArray(primary)) {
      return primary[lang] || primary.en || primary.mr || primary.hi || '';
    }
    if (translations && typeof translations === 'object' && !Array.isArray(translations)) {
      return translations[lang] || primary || translations.en || translations.mr || translations.hi || '';
    }
    return primary || '';
  }

  function getLocalizedFeatures(item, lang = currentLang) {
    if (Array.isArray(item?.features)) {
      if (lang === 'en') return item.features;
      const translated = item?.translations?.features?.[lang];
      return Array.isArray(translated) && translated.length > 0 ? translated : item.features;
    }
    if (item?.features && typeof item.features === 'object') {
      return item.features[lang] || item.features.en || [];
    }
    return [];
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!LANGUAGES.every(lang => form.name?.[lang]?.trim())) {
      setError('Plan name is required in English, Marathi, and Hindi');
      return;
    }
    if (!LANGUAGES.every(lang => form.description?.[lang]?.trim())) {
      setError('Description is required in English, Marathi, and Hindi');
      return;
    }
    if (!LANGUAGES.every(lang => form.features?.[lang]?.trim())) {
      setError('Features are required in English, Marathi, and Hindi');
      return;
    }
    
    // Validate pricing based on model
    if (form.pricingModel === 'fixed') {
      if (!form.price || form.price === '') {
        setError('Price is required for fixed pricing model');
        return;
      }
    }
    
    if (form.pricingModel === 'worker-based') {
      if (!form.smallIndustryPrice || form.smallIndustryPrice === '' || !form.perWorkerPrice || form.perWorkerPrice === '') {
        setError('Small industry price and per-worker price are required for worker-based pricing');
        return;
      }
    }
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const payload = {
        name: form.name.en.trim(),
        type: form.type || 'unified',
        alternativeName: (form.alternativeName.en || '').trim(),
        description: form.description.en.trim(),
        pricingModel: form.pricingModel || 'worker-based',
        smallIndustryThreshold: Number(form.smallIndustryThreshold) || 10,
        smallIndustryPrice: Number(form.smallIndustryPrice) || 5000,
        perWorkerPrice: Number(form.perWorkerPrice) || 100,
        duration: Number(form.duration) || 12,
        durationUnit: form.durationUnit || 'Months',
        features: toFeatureArray(form.features.en),
        translations: {
          name: {
            mr: (form.name.mr || '').trim(),
            hi: (form.name.hi || '').trim()
          },
          alternativeName: {
            mr: (form.alternativeName.mr || '').trim(),
            hi: (form.alternativeName.hi || '').trim()
          },
          description: {
            mr: (form.description.mr || '').trim(),
            hi: (form.description.hi || '').trim()
          },
          features: {
            mr: toFeatureArray(form.features.mr),
            hi: toFeatureArray(form.features.hi)
          },
          trainingProgram: {
            trainingAreas: {
              mr: Array.isArray(form.trainingProgramTranslations?.trainingAreas?.mr) ? form.trainingProgramTranslations.trainingAreas.mr : [],
              hi: Array.isArray(form.trainingProgramTranslations?.trainingAreas?.hi) ? form.trainingProgramTranslations.trainingAreas.hi : []
            },
            outcomes: {
              mr: Array.isArray(form.trainingProgramTranslations?.outcomes?.mr) ? form.trainingProgramTranslations.outcomes.mr : [],
              hi: Array.isArray(form.trainingProgramTranslations?.outcomes?.hi) ? form.trainingProgramTranslations.outcomes.hi : []
            }
          }
        },
        trainingProgram: form.trainingProgram || {
          enabled: false,
          duration: '52-Weeks',
          modes: [],
          trainingAreas: [],
          modules: [],
          skillMapping: [],
          outcomes: []
        },
        isActive: !!form.isActive
      };
      
      const method = editingId ? 'PUT' : 'POST';
      const url = editingId 
        ? `${DEFAULT_BASE_URL.replace(/\/$/, '')}/membership-plans/${editingId}` 
        : `${DEFAULT_BASE_URL.replace(/\/$/, '')}/membership-plans`;
      
      const res = await fetch(url, {
        method,
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('ifm_admin_token')}`
        },
        body: JSON.stringify(payload)
      });
      
      if (!res.ok) {
        const errorData = await res.json().catch(() => ({}));
        throw new Error(errorData.error || errorData.message || 'Save failed');
      }
      
      setForm(emptyForm);
      setEditingId(null);
      setShowFormModal(false);
      setSuccess(editingId ? 'Plan updated successfully!' : 'Plan created successfully!');
      await refresh();
      setTimeout(() => setSuccess(''), 3000);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(id) {
    if (!window.confirm('Delete this plan?')) return;
    try {
      const url = `${DEFAULT_BASE_URL.replace(/\/$/, '')}/membership-plans/${id}`;
      const res = await fetch(url, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('ifm_admin_token')}`
        }
      });
      if (res.ok) {
        setSuccess('Plan deleted successfully!');
        await refresh();
        setTimeout(() => setSuccess(''), 3000);
      } else {
        throw new Error('Delete failed');
      }
    } catch (e) {
      setError(e.message || 'Delete failed');
    }
  }

  function startEdit(item) {
    setEditingId(item._id);
    setForm({
      name: normalizeLocalizedText(item.name, item.translations?.name),
      type: item.type || 'unified',
      alternativeName: normalizeLocalizedText(item.alternativeName, item.translations?.alternativeName),
      description: normalizeLocalizedText(item.description, item.translations?.description),
      pricingModel: item.pricingModel || 'worker-based',
      smallIndustryThreshold: item.smallIndustryThreshold || 10,
      smallIndustryPrice: item.smallIndustryPrice || 5000,
      perWorkerPrice: item.perWorkerPrice || 100,
      duration: item.duration || 12,
      durationUnit: item.durationUnit || 'Months',
      features: normalizeLocalizedFeatures(item.features, item.translations?.features),
      trainingProgram: item.trainingProgram || {
        enabled: false,
        duration: '52-Weeks',
        modes: [],
        trainingAreas: [],
        modules: [],
        skillMapping: [],
        outcomes: []
      },
      trainingProgramTranslations: normalizeTrainingProgramTranslations(item.translations?.trainingProgram),
      isActive: !!item.isActive
    });
    setActiveLang('en');
    setShowFormModal(true);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyForm);
    setActiveLang('en');
    setError('');
    setSuccess('');
    setShowFormModal(false);
  }

  function openCreateForm() {
    resetForm();
    setShowFormModal(true);
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-blue-50">
      <header className="bg-white border-b shadow-sm sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button 
              onClick={() => navigate('/admin')} 
              className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600" />
            </button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">{'Membership Plans'}</h1>
              <p className="text-xs text-gray-500">Create and manage membership plans</p>
            </div>
          </div>
          <button
            onClick={openCreateForm}
            className="inline-flex items-center gap-2 px-6 py-2.5 bg-gradient-to-r from-primary-600 to-primary-700 text-white hover:text-black rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-md hover:shadow-lg font-semibold text-sm"
          >
            <Plus className="h-5 w-5" />
            {'Create Membership Plan'}
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="mb-6 p-4 rounded-xl bg-red-50 border-l-4 border-red-500 text-red-700 flex items-center justify-between shadow-sm">
            <span className="font-medium">{error}</span>
            <button onClick={() => setError('')} className="text-red-500 hover:text-red-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}
        
        {success && (
          <div className="mb-6 p-4 rounded-xl bg-green-50 border-l-4 border-green-500 text-green-700 flex items-center justify-between shadow-sm">
            <div className="flex items-center gap-2">
              <Check className="h-5 w-5" />
              <span className="font-medium">{success}</span>
            </div>
            <button onClick={() => setSuccess('')} className="text-green-500 hover:text-green-700">
              <X className="h-5 w-5" />
            </button>
          </div>
        )}

        {/* Form Modal */}
        {showFormModal && (
          <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-end bg-black bg-opacity-50 backdrop-blur-sm">
            <div className="bg-white shadow-2xl w-full sm:w-[92vw] md:w-[84vw] lg:w-[72vw] xl:w-[62vw] 2xl:w-[54vw] max-w-[980px] h-full sm:h-[calc(100vh-32px)] sm:my-4 flex flex-col rounded-none sm:rounded-2xl overflow-x-hidden min-w-0">
              {/* Modal Header */}
              <div className="px-4 sm:px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-4 bg-gradient-to-r from-primary-50 to-white sticky top-0 z-10">
                <div>
                  <h2 className="text-lg sm:text-2xl font-bold text-gray-900">
                    {editingId ? 'Edit Membership Plan' : 'Create New Membership Plan'}
                  </h2>
                  <p className="text-sm text-gray-500 mt-1">
                    {editingId ? 'Update the plan details below' : 'Fill in all fields to create a new membership plan'}
                  </p>
                </div>
                <button
                  onClick={resetForm}
                  className="p-2 rounded-lg hover:bg-gray-100 transition-colors"
                  title="Close"
                >
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>

              {/* Modal Content */}
              <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-5">
                <form onSubmit={handleSubmit} className="space-y-6" id="membership-plan-form">
            {/* Basic Information */}
            <div className="space-y-6">
              <h3 className="text-lg font-bold text-gray-900 border-b pb-2">Basic Information</h3>

              <div className="border-b border-gray-200 pb-2">
                <div className="flex flex-wrap gap-2">
                  {LANGUAGES.map((lang) => (
                    <button
                      key={lang}
                      type="button"
                      onClick={() => setActiveLang(lang)}
                      className={`px-4 py-2 text-sm font-semibold rounded-t-lg transition-colors ${
                        activeLang === lang
                          ? 'bg-primary-600 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {LANGUAGE_LABELS[lang]}
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  Plan Name, Description, and Features are mandatory in English, Marathi, and Hindi.
                </p>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Plan Name ({LANGUAGE_LABELS[activeLang]}) <span className="text-red-500">*</span>
                </label>
                <input
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={form.name?.[activeLang] || ''}
                  onChange={e => setForm({
                    ...form,
                    name: { ...form.name, [activeLang]: e.target.value }
                  })}
                  placeholder={`Enter plan name in ${LANGUAGE_LABELS[activeLang]}`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Alternative Name ({LANGUAGE_LABELS[activeLang]}) (Optional)
                </label>
                <input
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                  value={form.alternativeName?.[activeLang] || ''}
                  onChange={e => setForm({
                    ...form,
                    alternativeName: { ...form.alternativeName, [activeLang]: e.target.value }
                  })}
                  placeholder={`Enter alternative name in ${LANGUAGE_LABELS[activeLang]}`}
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Description ({LANGUAGE_LABELS[activeLang]}) <span className="text-red-500">*</span>
                </label>
                <textarea
                  rows={3}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y"
                  value={form.description?.[activeLang] || ''}
                  onChange={e => setForm({
                    ...form,
                    description: { ...form.description, [activeLang]: e.target.value }
                  })}
                  placeholder={`Enter description in ${LANGUAGE_LABELS[activeLang]}`}
                  required
                />
              </div>

              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Duration <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    value={form.duration} 
                    onChange={e => setForm({ ...form, duration: e.target.value })} 
                    required
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Duration Unit <span className="text-red-500">*</span>
                  </label>
                  <select
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    value={form.durationUnit}
                    onChange={e => setForm({ ...form, durationUnit: e.target.value })}
                    required
                  >
                    <option value="Days">Days</option>
                    <option value="Months">Months</option>
                    <option value="Years">Years</option>
                  </select>
                </div>
              </div>
            </div>

            {/* Pricing Configuration */}
            <div className="border-t-2 border-gray-200 pt-6 space-y-6">
              <h3 className="text-lg font-bold text-gray-900">Pricing Configuration</h3>
              
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Small Industry Threshold (Workers) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    value={form.smallIndustryThreshold} 
                    onChange={e => setForm({ ...form, smallIndustryThreshold: e.target.value })} 
                    min="1"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Up to this many workers</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Small Industry Price (INR) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    value={form.smallIndustryPrice} 
                    onChange={e => setForm({ ...form, smallIndustryPrice: e.target.value })} 
                    min="0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">One-time fee for small industries</p>
                </div>
                
                <div>
                  <label className="block text-sm font-semibold text-gray-700 mb-2">
                    Per Worker Price (INR) <span className="text-red-500">*</span>
                  </label>
                  <input 
                    type="number"
                    className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                    value={form.perWorkerPrice} 
                    onChange={e => setForm({ ...form, perWorkerPrice: e.target.value })} 
                    min="0"
                    required
                  />
                  <p className="text-xs text-gray-500 mt-1">Price per worker for larger industries</p>
                </div>
              </div>
            </div>

            {/* Features */}
            <div className="border-t-2 border-gray-200 pt-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4">Plan Features</h3>
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Features (One per line) <span className="text-red-500">*</span>
                </label>
                <textarea 
                  rows={8}
                  className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm" 
                  value={form.features?.[activeLang] || ''} 
                  onChange={e => setForm({ ...form, features: { ...form.features, [activeLang]: e.target.value } })} 
                  placeholder="- One Membership | One Voice | Complete Industrial Support
- Equal representation & regulatory support
- Government coordination & liaison
- Continuous workforce development
- Access to 52-Week Workers Training Program"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Enter each benefit on a new line, starting with - if needed</p>
              </div>
            </div>

            {/* Training Program */}
            <div className="border-t-2 border-gray-200 pt-6 space-y-6">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-gray-900">52-Week Workers Training Program</h3>
                <label className="flex items-center gap-3 p-3 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                  <input 
                    type="checkbox"
                    checked={form.trainingProgram?.enabled || false}
                    onChange={e => setForm(prev => ({
                      ...prev,
                      trainingProgram: { ...prev.trainingProgram, enabled: e.target.checked }
                    }))}
                    className="w-5 h-5 text-primary-600 rounded"
                  />
                  <span className="font-medium">Enable Training Program</span>
                </label>
              </div>

              {form.trainingProgram?.enabled && (
                <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Duration
                    </label>
                    <input 
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent" 
                      value={form.trainingProgram.duration || '52-Weeks'} 
                      onChange={e => setForm(prev => ({
                        ...prev,
                        trainingProgram: { ...prev.trainingProgram, duration: e.target.value }
                      }))} 
                      placeholder="52-Weeks"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Modes (One per line)
                    </label>
                    <textarea 
                      rows={3}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm" 
                      value={(form.trainingProgram.modes || []).join('\n')} 
                      onChange={e => setForm(prev => ({
                        ...prev,
                        trainingProgram: { ...prev.trainingProgram, modes: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }
                      }))} 
                      placeholder="online\noffline\nhybrid"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Areas (One per line)
                    </label>
                    <textarea 
                      rows={5}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm" 
                      value={(form.trainingProgram.trainingAreas || []).join('\n')} 
                      onChange={e => setForm(prev => ({
                        ...prev,
                        trainingProgram: { ...prev.trainingProgram, trainingAreas: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }
                      }))} 
                      placeholder="Industrial Safety & Occupational Health
Pollution Control & Environmental Compliance
Labour Laws, Discipline & Workforce Awareness"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Training Outcomes (One per line)
                    </label>
                    <textarea 
                      rows={4}
                      className="w-full border-2 border-gray-300 rounded-lg px-4 py-3 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent resize-y font-mono text-sm" 
                      value={(form.trainingProgram.outcomes || []).join('\n')} 
                      onChange={e => setForm(prev => ({
                        ...prev,
                        trainingProgram: { ...prev.trainingProgram, outcomes: e.target.value.split('\n').map(s => s.trim()).filter(Boolean) }
                      }))} 
                      placeholder="Reduced accidents & safety violations
Improved worker efficiency & productivity
Better compliance during inspections"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Status */}
            <div className="border-t-2 border-gray-200 pt-6">
              <label className="flex items-center gap-3 p-4 border-2 border-gray-300 rounded-lg cursor-pointer hover:bg-gray-50">
                <input 
                  type="checkbox"
                  checked={form.isActive}
                  onChange={e => setForm({ ...form, isActive: e.target.checked })}
                  className="w-5 h-5 text-primary-600 rounded"
                />
                <span className="font-medium">Plan is Active</span>
              </label>
            </div>
            
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-4 pt-6 border-t border-gray-200">
              <button 
                type="submit" 
                disabled={saving} 
                className="inline-flex items-center gap-2 bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-lg hover:from-primary-700 hover:to-primary-800 disabled:opacity-60 disabled:cursor-not-allowed transition-all shadow-lg font-semibold w-full sm:w-auto justify-center"
              >
                <Save className="h-5 w-5" /> 
                {saving ? 'Saving...' : editingId ? 'Update Plan' : 'Create Plan'}
              </button>
              <button 
                type="button" 
                onClick={resetForm} 
                className="px-6 py-3 rounded-lg border-2 border-gray-300 hover:bg-gray-50 transition-colors font-medium w-full sm:w-auto"
              >
                Cancel
              </button>
            </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-5">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-gray-500" />
              <h3 className="text-sm font-semibold text-gray-900">Filters</h3>
              {activeFiltersCount > 0 && (
                <span className="px-2 py-0.5 bg-primary-100 text-primary-700 text-xs font-semibold rounded-full">
                  {activeFiltersCount} active
                </span>
              )}
            </div>
            {activeFiltersCount > 0 && (
              <button
                onClick={clearFilters}
                className="flex items-center gap-1 text-xs text-primary-600 hover:text-primary-700 font-medium transition-colors"
              >
                <X className="h-3.5 w-3.5" />
                Clear All
              </button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {/* Name Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Name
              </label>
              <div className="relative">
                <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  className={`w-full border border-gray-300 rounded-lg pl-9 ${filters.name ? 'pr-9' : 'pr-3'} py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all`}
                  placeholder="Search name..."
                  value={filters.name}
                  onChange={(e) => handleFilterChange('name', e.target.value)}
                />
                {filters.name && (
                  <button
                    onClick={() => handleFilterChange('name', '')}
                    className="absolute right-2.5 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
                    type="button"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>

            {/* Active Status Filter */}
            <div>
              <label className="block text-xs font-medium text-gray-700 mb-1.5">
                Status
              </label>
              <select
                className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                value={filters.isActive}
                onChange={(e) => handleFilterChange('isActive', e.target.value)}
              >
                <option value="">All Statuses</option>
                <option value="true">Active</option>
                <option value="false">Inactive</option>
              </select>
            </div>
          </div>
        </div>

        {/* List */}
        <div className="bg-white shadow-xl rounded-2xl border border-gray-200 overflow-hidden">
          <div className="px-8 py-6 border-b border-gray-200 bg-gradient-to-r from-gray-50 to-white">
            <p className="text-sm text-gray-500">
              {items.length} plan{items.length !== 1 ? 's' : ''} {activeFiltersCount > 0 ? 'found' : 'total'}
            </p>
          </div>
          
          <div className="divide-y divide-gray-200">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
                <p className="text-gray-600">Loading plans...</p>
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center">
                <p className="text-gray-600 mb-4 font-medium">No membership plans yet.</p>
                <button 
                  onClick={openCreateForm}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-lg hover:from-primary-700 hover:to-primary-800 transition-all shadow-lg font-semibold"
                >
                  <Plus className="h-5 w-5" /> Create Your First Plan
                </button>
              </div>
            ) : (
              items.map(item => (
                <div key={item._id} className="p-6 flex flex-col lg:flex-row lg:items-start justify-between gap-6 hover:bg-gradient-to-r hover:from-gray-50 hover:to-blue-50 transition-all">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start gap-3 mb-3 flex-wrap">
                      <div>
                        <h4 className="font-bold text-gray-900 text-lg">{getLocalizedValue(item, 'name')}</h4>
                        {getLocalizedValue(item, 'alternativeName') && (
                          <p className="text-sm text-gray-600 italic">{getLocalizedValue(item, 'alternativeName')}</p>
                        )}
                      </div>
                      <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                        item.tier === 'Bronze' ? 'bg-amber-100 text-amber-800' :
                        item.tier === 'Silver' ? 'bg-gray-100 text-gray-800' :
                        item.tier === 'Gold' ? 'bg-yellow-100 text-yellow-800' :
                        item.tier === 'Unified' ? 'bg-blue-100 text-blue-800' :
                        'bg-purple-100 text-purple-800'
                      }`}>
                        {item.tier || (item.type === 'unified' ? 'Unified' : 'Standard')}
                      </span>
                      {item.type === 'unified' && (
                        <span className="px-3 py-1 rounded-full text-xs font-semibold bg-green-100 text-green-800">
                          Unified Plan
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mb-3 break-words">{getLocalizedValue(item, 'description')}</p>
                    <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500 mb-3 break-words">
                      {item.pricingModel === 'worker-based' ? (
                        <span className="font-semibold break-words">
                          Pricing: Worker-Based (&lt;={item.smallIndustryThreshold || 10} workers: INR {Number(item.smallIndustryPrice || 5000).toLocaleString('en-IN')}, &gt;{item.smallIndustryThreshold || 10} workers: INR {Number(item.smallIndustryPrice || 5000).toLocaleString('en-IN')} base + INR {item.perWorkerPrice || 100}/additional worker)
                        </span>
                      ) : (
                        <span className="font-semibold break-words">Price: INR {item.price ? Number(item.price).toLocaleString('en-IN') : '0'}</span>
                      )}
                      <span>|</span>
                      <span>Duration: {item.duration} {item.durationUnit}</span>
                      <span>|</span>
                      <span>Units: {item.unitsCovered}</span>
                      {item.responseTime && (
                        <>
                          <span>|</span>
                          <span>Response: {item.responseTime}</span>
                        </>
                      )}
                      {item.type === 'unified' && item.trainingProgram && item.trainingProgram.enabled && (
                        <>
                          <span>|</span>
                          <span className="text-green-600 font-semibold">Training: {item.trainingProgram.duration} weeks</span>
                        </>
                      )}
                    </div>
                    {getLocalizedFeatures(item).length > 0 && (
                      <ul className="text-sm text-gray-700 space-y-1">
                        {getLocalizedFeatures(item).slice(0, 3).map((f, i) => (
                          <li key={i} className="flex items-start gap-2">
                            <span className="text-green-600 mt-0.5">*</span>
                            <span className="line-clamp-1">{f}</span>
                          </li>
                        ))}
                        {getLocalizedFeatures(item).length > 3 && (
                          <li className="text-gray-500">+ {getLocalizedFeatures(item).length - 3} more benefits</li>
                        )}
                      </ul>
                    )}
                  </div>
                  <div className="flex items-center gap-2 flex-shrink-0 flex-wrap w-full lg:w-auto">
                    <button 
                      onClick={() => startEdit(item)} 
                      className="p-3 border-2 border-blue-200 rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-all"
                      title="Edit"
                    >
                      <Edit2 className="h-5 w-5 text-blue-600" />
                    </button>
                    <button 
                      onClick={() => handleDelete(item._id)} 
                      className="p-3 border-2 border-red-200 rounded-lg hover:bg-red-50 hover:border-red-300 transition-all"
                      title="Delete"
                    >
                      <Trash2 className="h-5 w-5 text-red-600" />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminMembershipPlansPage;



